import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useTeamConfig } from '../../../context/TeamConfigContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import EquipmentImageUpload from './EquipmentImageUpload';

const CreateEquipmentPage = () => {
  const navigate = useNavigate();
  const { catalogs, createEquipment, createEquipmentFromCatalog } = useEquipment();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { locations, departments, wards } = useTeamConfig();

  const [useCatalog, setUseCatalog] = useState(true);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    subCategory: '',
    type: EQUIPMENT_TYPES.SMALL_ITEM,
    size: '',
    unit: '',
    color: '',
    brand: '',
    model: '',
    stockQuantity: '',
    barcode: '',
    serialNumber: '',
    currentLocation: '',
    department: '',
    ward: '',
    minStock: '',
    maxStock: '',
    images: []
  });

  const [loading, setLoading] = useState(false);
  const [searchCatalogTerm, setSearchCatalogTerm] = useState('');
  const [showAdvancedStock, setShowAdvancedStock] = useState(false); // สำหรับซ่อน/แสดง Stock ต่ำสุด/สูงสุด

  // Predefined options (same as Catalog)
  const categoryOptions = [
    'อุปกรณ์ทางการแพทย์',
    'อุปกรณ์ดูแลผู้ป่วย',
    'อุปกรณ์ฉุกเฉิน',
    'อุปกรณ์ทั่วไป',
    'อุปกรณ์ห้องผ่าตัด',
    'อุปกรณ์ห้องคลอด'
  ];

  const subCategoryOptions = {
    'อุปกรณ์ทางการแพทย์': [
      'อุปกรณ์ให้สารน้ำ',
      'อุปกรณ์ช่วยหายใจ',
      'อุปกรณ์วัดสัญญาณชีพ',
      'อุปกรณ์ฉีดยา',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ดูแลผู้ป่วย': [
      'อุปกรณ์ทำความสะอาด',
      'อุปกรณ์จัดเก็บ',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ฉุกเฉิน': [
      'อุปกรณ์ช่วยชีวิต',
      'อุปกรณ์ปฐมพยาบาล',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ทั่วไป': [
      'อุปกรณ์สำนักงาน',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ห้องผ่าตัด': [
      'อุปกรณ์ผ่าตัด',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ห้องคลอด': [
      'อุปกรณ์คลอด',
      'อุปกรณ์อื่นๆ'
    ]
  };

  const unitOptions = [
    'เส้น',
    'ชิ้น',
    'กล่อง',
    'เครื่อง',
    'ม้วน',
    'คู่',
    'ชุด',
    'ขวด',
    'กระป๋อง',
    'ถุง',
    'แผ่น',
    'อัน',
    'ตัว',
    'เล่ม'
  ];

  const colorOptions = [
    'ใส',
    'ขาว',
    'ฟ้า',
    'เขียว',
    'เหลือง',
    'แดง',
    'ชมพู',
    'ม่วง',
    'ดำ',
    'เทา',
    'อื่นๆ'
  ];

  // Use config from TeamConfigContext (fallback to default if not loaded)
  const locationOptions = locations && locations.length > 0 ? [...locations, 'อื่นๆ'] : [
    'ตู้เก็บอุปกรณ์ 1',
    'ตู้เก็บอุปกรณ์ 2',
    'ตู้เก็บอุปกรณ์ 3',
    'ห้องเก็บอุปกรณ์',
    'ห้อง ICU',
    'ห้องผ่าตัด',
    'ห้องคลอด',
    'ห้องฉุกเฉิน',
    'อื่นๆ'
  ];

  const departmentOptions = departments && departments.length > 0 ? [...departments, 'แผนกอื่นๆ'] : [
    'อายุรกรรม',
    'ศัลยกรรม',
    'สูติ-นรีเวช',
    'กุมารเวช',
    'ICU',
    'ห้องผ่าตัด',
    'ห้องคลอด',
    'ห้องฉุกเฉิน',
    'แผนกอื่นๆ'
  ];

  const wardOptions = wards && wards.length > 0 ? [...wards, 'หอผู้ป่วยอื่นๆ'] : [
    'หอผู้ป่วยอายุรกรรม',
    'หอผู้ป่วยศัลยกรรม',
    'หอผู้ป่วยสูติ-นรีเวช',
    'หอผู้ป่วยกุมารเวช',
    'หอผู้ป่วยวิกฤต (ICU)',
    'หอผู้ป่วยอื่นๆ'
  ];

  // Filter catalogs
  const filteredCatalogs = catalogs.filter(catalog => {
    if (!searchCatalogTerm) return true;
    return catalog.name?.toLowerCase().includes(searchCatalogTerm.toLowerCase()) ||
           catalog.code?.toLowerCase().includes(searchCatalogTerm.toLowerCase());
  });

  // Get selected catalog
  const selectedCatalog = catalogs.find(c => c.id === selectedCatalogId);

  // Auto-fill from catalog
  React.useEffect(() => {
    if (selectedCatalog && useCatalog) {
      setFormData(prev => ({
        ...prev,
        name: selectedCatalog.name || '',
        code: selectedCatalog.code || '',
        category: selectedCatalog.category || '',
        subCategory: selectedCatalog.subCategory || '',
        type: selectedCatalog.type || EQUIPMENT_TYPES.SMALL_ITEM,
        size: selectedCatalog.size || '',
        unit: selectedCatalog.unit || '',
        color: selectedCatalog.color || '',
        brand: selectedCatalog.brand || '',
        model: selectedCatalog.model || '',
        // ดึง Stock Settings จาก Catalog เป็นค่าเริ่มต้น
        minStock: selectedCatalog.minStock !== null && selectedCatalog.minStock !== undefined ? selectedCatalog.minStock : '',
        maxStock: selectedCatalog.maxStock !== null && selectedCatalog.maxStock !== undefined ? selectedCatalog.maxStock : '',
        stockQuantity: '', // Stock เริ่มต้นให้กรอกเอง (เริ่มต้นที่ 0)
        currentLocation: selectedCatalog.defaultLocation || '',
        department: selectedCatalog.defaultDepartment || '',
        ward: selectedCatalog.defaultWard || ''
      }));
    }
  }, [selectedCatalog, useCatalog]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentTeam) {
      alert('กรุณาเข้าสู่ระบบและเลือกทีม');
      return;
    }

    setLoading(true);

    try {
      if (useCatalog && selectedCatalogId) {
        // Create from Catalog
        const additionalData = {
          size: formData.size,
          color: formData.color,
          stockQuantity: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? parseInt(formData.stockQuantity) || 0 : null,
          barcode: formData.type === EQUIPMENT_TYPES.LARGE_ITEM ? formData.barcode : null,
          serialNumber: formData.serialNumber || null,
          currentLocation: formData.currentLocation || '',
          department: formData.department || '',
          ward: formData.ward || ''
        };

        const result = await createEquipmentFromCatalog(selectedCatalogId, additionalData);
        
        if (result.success) {
          alert('✅ สร้างอุปกรณ์สำเร็จ\n\n📋 อุปกรณ์ถูกส่งให้หัวหน้าพิจารณาแล้ว\nรอการอนุมัติก่อนใช้งาน');
          navigate('/nurse/equipment');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } else {
        // Create new equipment
        const equipmentData = {
          name: formData.name,
          code: formData.code,
          category: formData.category,
          subCategory: formData.subCategory || null,
          type: formData.type,
          size: formData.size || null,
          unit: formData.unit,
          color: formData.color || null,
          brand: formData.brand || null,
          model: formData.model || null,
          stockQuantity: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? parseInt(formData.stockQuantity) || 0 : null,
          barcode: formData.type === EQUIPMENT_TYPES.LARGE_ITEM ? formData.barcode : null,
          serialNumber: formData.serialNumber || null,
          currentLocation: formData.currentLocation || '',
          department: formData.department || '',
          ward: formData.ward || '',
          minStock: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? (formData.minStock ? parseInt(formData.minStock) : null) : null,
          maxStock: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? (formData.maxStock ? parseInt(formData.maxStock) : null) : null,
          images: formData.images.map(img => img.url)
        };

        const result = await createEquipment(equipmentData);
        
        if (result.success) {
          alert('✅ สร้างอุปกรณ์สำเร็จ\n\n📋 อุปกรณ์ถูกส่งให้หัวหน้าพิจารณาแล้ว\nรอการอนุมัติก่อนใช้งาน');
          navigate('/nurse/equipment');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      }
    } catch (err) {
      console.error('Error creating equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">เพิ่มอุปกรณ์</h1>
        </div>

        {/* Catalog Selection */}
        {useCatalog && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">เลือกจาก Catalog</h2>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 ค้นหา Catalog..."
                value={searchCatalogTerm}
                onChange={(e) => setSearchCatalogTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {filteredCatalogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ไม่พบ Catalog</p>
                <button
                  onClick={() => navigate('/nurse/equipment/catalog')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  สร้าง Catalog ใหม่
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {filteredCatalogs.map(catalog => (
                  <button
                    key={catalog.id}
                    onClick={() => setSelectedCatalogId(catalog.id)}
                    className={`p-4 border-2 rounded-lg text-left transition ${
                      selectedCatalogId === catalog.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-800">{catalog.name}</h3>
                        <p className="text-sm text-gray-600">{catalog.code}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {catalog.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'} | {catalog.unit}
                        </p>
                      </div>
                      {selectedCatalogId === catalog.id && (
                        <span className="text-blue-600 text-xl">✓</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setUseCatalog(false)}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                หรือ ➕ สร้างใหม่ (ไม่ใช้ Catalog)
              </button>
            </div>
          </div>
        )}

        {!useCatalog && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-700">กำลังสร้างอุปกรณ์ใหม่ (ไม่ใช้ Catalog)</p>
              <button
                onClick={() => {
                  setUseCatalog(true);
                  setFormData({
                    name: '',
                    code: '',
                    category: '',
                    subCategory: '',
                    type: EQUIPMENT_TYPES.SMALL_ITEM,
                    size: '',
                    unit: '',
                    color: '',
                    brand: '',
                    model: '',
                    stockQuantity: '',
                    barcode: '',
                    serialNumber: '',
                    currentLocation: '',
                    department: '',
                    ward: '',
                    minStock: '',
                    maxStock: '',
                    images: []
                  });
                }}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                ใช้ Catalog แทน
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Catalog Info Display */}
          {useCatalog && selectedCatalog && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">ข้อมูลจาก Catalog</h3>
              <p className="text-sm text-gray-700">
                Catalog: {selectedCatalog.name} ({selectedCatalog.code})
              </p>
              <p className="text-xs text-gray-600 mt-1">
                ✅ ข้อมูลพื้นฐานจะถูกกรอกอัตโนมัติจาก Catalog
              </p>
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">ข้อมูลพื้นฐาน</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ชื่ออุปกรณ์ *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={useCatalog && selectedCatalog}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  รหัสอุปกรณ์ *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={useCatalog && selectedCatalog}
                />
              </div>
            </div>
          </div>

          {/* Category & Type */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">หมวดหมู่</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  หมวดหมู่ *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    category: e.target.value,
                    subCategory: ''
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={useCatalog && selectedCatalog}
                >
                  <option value="">-- เลือกหมวดหมู่ --</option>
                  {categoryOptions.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              {formData.category && subCategoryOptions[formData.category] && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    หมวดหมู่ย่อย
                  </label>
                  <select
                    value={formData.subCategory}
                    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={useCatalog && selectedCatalog}
                  >
                    <option value="">-- เลือกหมวดหมู่ย่อย --</option>
                    {subCategoryOptions[formData.category].map(subCat => (
                      <option key={subCat} value={subCat}>{subCat}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ประเภท *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value={EQUIPMENT_TYPES.SMALL_ITEM}
                      checked={formData.type === EQUIPMENT_TYPES.SMALL_ITEM}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="mr-2"
                      disabled={useCatalog && selectedCatalog}
                    />
                    ชิ้นเล็ก (Stock)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value={EQUIPMENT_TYPES.LARGE_ITEM}
                      checked={formData.type === EQUIPMENT_TYPES.LARGE_ITEM}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="mr-2"
                      disabled={useCatalog && selectedCatalog}
                    />
                    ชิ้นใหญ่ (Individual)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Physical Properties */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">คุณสมบัติ</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ขนาด
                </label>
                <input
                  type="text"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น 18G, 20G"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  หน่วย *
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={useCatalog && selectedCatalog}
                >
                  <option value="">-- เลือกหน่วย --</option>
                  {unitOptions.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  สี
                </label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- เลือกสี --</option>
                  {colorOptions.map(color => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stock Settings (Small Items) */}
          {formData.type === EQUIPMENT_TYPES.SMALL_ITEM && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">ตั้งค่า Stock</h3>
              {useCatalog && selectedCatalog && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">
                    💡 <strong>หมายเหตุ:</strong> ค่า Stock ต่ำสุด/สูงสุด ถูกดึงมาจาก Catalog "{selectedCatalog.name}" 
                    เป็นค่าเริ่มต้น คุณสามารถแก้ไขได้ตามต้องการ
                  </p>
                </div>
              )}
              
              {/* Stock เริ่มต้น (แสดงเสมอ) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  จำนวน Stock เริ่มต้น *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.stockQuantity}
                  onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 จำนวน Stock นี้สามารถใช้ได้เมื่อได้รับการอนุมัตินำเข้า stock
                </p>
              </div>

              {/* Stock ต่ำสุด/สูงสุด (ซ่อนขยาย) */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowAdvancedStock(!showAdvancedStock)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      ⚙️ การตั้งค่า Stock เพิ่มเติม
                    </span>
                    <span className="text-xs text-gray-500">(ไม่บังคับ)</span>
                  </div>
                  <span className={`text-gray-500 transition-transform ${showAdvancedStock ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {showAdvancedStock && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock ต่ำสุด
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.minStock}
                          onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={useCatalog && selectedCatalog?.minStock ? selectedCatalog.minStock : "เช่น 50"}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          จำนวน Stock ต่ำสุดที่ควรมี (สำหรับแจ้งเตือน)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock สูงสุด
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.maxStock}
                          onChange={(e) => setFormData({ ...formData, maxStock: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={useCatalog && selectedCatalog?.maxStock ? selectedCatalog.maxStock : "เช่น 200"}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          จำนวน Stock สูงสุดที่ควรเก็บ (สำหรับแจ้งเตือน)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Brand & Model (Large Items) */}
          {formData.type === EQUIPMENT_TYPES.LARGE_ITEM && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">ยี่ห้อ/รุ่น</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ยี่ห้อ
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    รุ่น
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Barcode
                  </label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="เช่น IP-001-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">ตำแหน่งเก็บอุปกรณ์</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ตำแหน่งเก็บอุปกรณ์
                </label>
                <select
                  value={formData.currentLocation}
                  onChange={(e) => setFormData({ ...formData, currentLocation: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- เลือกตำแหน่งเก็บอุปกรณ์ --</option>
                  {locationOptions.map((loc, index) => (
                    <option key={`loc-${index}-${loc}`} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  แผนก
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- เลือกแผนก --</option>
                  {departmentOptions.map((dept, index) => (
                    <option key={`dept-${index}-${dept}`} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  หอผู้ป่วย
                </label>
                <select
                  value={formData.ward}
                  onChange={(e) => setFormData({ ...formData, ward: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- เลือกหอผู้ป่วย --</option>
                  {wardOptions.map((ward, index) => (
                    <option key={`ward-${index}-${ward}`} value={ward}>{ward}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Images */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">รูปภาพ</h3>
            <EquipmentImageUpload
              images={formData.images}
              onChange={(images) => setFormData({ ...formData, images })}
              equipmentId={null}
              teamId={currentTeam?.id}
              userPlan={currentUser?.plan || 'FREE'}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/nurse/equipment')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading || (useCatalog && !selectedCatalogId)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEquipmentPage;

